import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../store/hooks";
import { downloadDict, uploadObject } from "../../../rest/data";
import { Abbreviation, Book, BookForList, BookLanguagePreset, Language, PresetLanguage, copyRestObject } from "../../../rest/models";
import { bookEditorActions } from './slice';
import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import { ErnotsConfig, GetPageTitle } from "../../../ErnotsConfig";
import { BookLanguagesEdit } from "./BookLanguagesEdit";
import { BookAbbreviationsEdit } from "./BookAbbreviationsEdit";
import { ModelMap } from "../../../rest/slice";
import { presetToText, recalculateBlocksAndSentences } from "../utils";
import { RequestStatusDisplay } from "../../CommonComponents";
import { Anchor, Button, Input } from "../../RequestRespectingInputs";
import { BookEditExplanations } from "./explanation";
import { BookRichTextOptionsEdit } from "./BookRichTextOptionsEdit";
import { BookTranslationsEdit } from "./BookTranslationsEdit";
import { RequestStatus } from "../../../api_request/api_request";
import { makePlainObject } from "../../../utils";
import { BookBasicOptionsEdit } from "./BookBasicOptionsEdit";

/** The main component for the create/edit book page. */
export function BookEditForm(props: Props)
{
    const appDispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    
    //This is the object used to fill the form, while the one from the database map is untouched.
    const formBook = props.formBook;
    const [lastSavedSourceText, setLastSavedSourceText] = useState(formBook.source_text);
    const [bookUploadRequest, setBookUploadRequest] = useState<RequestStatus|undefined>(undefined);
    const [presetUploadRequest, setPresetUploadRequest] = useState<RequestStatus|undefined>(undefined);
    const [bookListDownloadRequest, setBookListDownloadRequest] = useState<RequestStatus|undefined>(undefined);

    //Changes must be saved to get accurate translations.
    const sourceTextChanged = lastSavedSourceText !== formBook.source_text;

    //This memoizes the values formBook has right when we mount.
    //eslint-disable-next-line react-hooks/exhaustive-deps
    //See the props of BookAbbreviationsEdit, where it's actually used.
    const abbrsByLang = useMemo(function()
    {
        const ret = {} as {[langId: string]: string|undefined};
        ret[formBook.source_language] = formBook.abbreviations;

        return ret;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    //Runs only when the form first loads.
    useEffect(function()
    {
        //Adds languages in the preset to the book.
        if (props.preset && !formBook.id)
        {
            appDispatch(bookEditorActions.setSourceLanguage(props.preset.source_language));
            for (const lang of props.preset.presetlanguage_set)
            {
                appDispatch(bookEditorActions.addBookLanguage(lang.language));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    //Executed when the form first loads, and also when a new book is saved and formBook.id becomes available.
    useEffect(function()
    {
        const searchParams = new URLSearchParams(location.search);

        //Set the parameter 'bookId' to match formBook.id, if there is one.
        if (formBook.id)
        {
            searchParams.set('bookId', formBook.id);
            
            const to = 
            {
                pathname: location.pathname,
                search: searchParams + ''
            }
            navigate(to, {replace: true});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formBook.id]);

    //Executed when an upload is finished. 
    useEffect(function()
    {
        if (!bookUploadRequest?.success || !bookUploadRequest?.responseJson)
        {
            return;
        }

        //Update the formBook.  
        const book = copyRestObject(bookUploadRequest.responseJson as Book, Book);
        appDispatch(bookEditorActions.setFormBook(book));
        appDispatch(bookEditorActions.sortAndSyncBookLanguages());
        setLastSavedSourceText(book.source_text);

        //Download a fresh dict for BookForList
        downloadDict(BookForList, appDispatch, setBookListDownloadRequest);
        
        //Create a new language preset.
        const newPreset = copyRestObject(new BookLanguagePreset(), BookLanguagePreset);
        newPreset.source_language = book.source_language;
        for (const bl of book.booklanguage_set)
        {
            const pl = copyRestObject(new PresetLanguage(), PresetLanguage);
            pl.language = bl.language; 
            pl.display_order = bl.display_order;
            
            newPreset.presetlanguage_set.push(pl);
        }

        const strPreset = presetToText(newPreset, props.langModels)?.comparable;
        const duplicates = Object
            .values(props.presetModels)
            .filter(otherPreset => strPreset === presetToText(otherPreset, props.langModels)?.comparable);

        if (strPreset && !duplicates.length)
        {
            uploadObject(newPreset, BookLanguagePreset, appDispatch, setPresetUploadRequest);
        }
        else
        {
            //So the message from a previous upload doesn't linger when it's not true.
            setPresetUploadRequest(makePlainObject(new RequestStatus()));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appDispatch, bookUploadRequest?.success, bookUploadRequest?.responseJson]);
    
    
    //This effect communicates with the BookPreview pages opened from this one.
    //They send a message to their parent window, and this function handles them.
    useEffect(function()
    {
        function receiveMessage(e: MessageEvent)
        {
            if (e.data === 'requestBookContents' && e.source)
            {
                e.source.postMessage(formBook, e.origin as any);
            }
        }

        window.addEventListener('message', receiveMessage, false);

        return function()
        {
            window.removeEventListener('message', receiveMessage);
        }
    });
    
    const isDuplicate = function()
    {
        const objs = Object.values(props.bookModels);
        const duplicates = objs
            .filter(b => b.title === formBook.title) //Objects with the same properties as the current form.
            .filter(b => b.key !== formBook.key); //Unless it's the same object that we're editing. It can equal itself.

        return duplicates.length > 0;
    }();

    function openPreview()
    {
        window.open(ErnotsConfig.BaseURL + 'book/preview', '_blank');
    }

    function submitForm(e: React.SyntheticEvent)
    {
        e.preventDefault();

        if (isDuplicate)
        {
            window.alert("This book's title is the same as another book's.");
            return;
        }

        if (formBook.manually_edited && !window.confirm('This book was changed in the reader view. If you save now, those changes could be lost.'))
        {
            return;
        }
        
        let formBook2 = recalculateBlocksAndSentences(formBook, appDispatch);
        formBook2.manually_edited = false;
        
        uploadObject(formBook2, Book, appDispatch, setBookUploadRequest);
    }

    let main = <></>;
    
    //<title/> tag contents.
    const pageTitle = `Editing "${formBook.title}"`

    const stepProperties =
    [
        {
            nextStepEnabled: !!(formBook.title && formBook.source_text),
            children:
                <div>
                    <BookBasicOptionsEdit formBook={formBook}/>
                    <br/><br/>
                    {formBook.is_html ? <BookRichTextOptionsEdit formBook={formBook}/> : <></>}
                </div>
        },
        {
            nextStepEnabled: !!(formBook.source_language),
            children:
                <BookLanguagesEdit
                    source_language={formBook.source_language}
                    book_languages={formBook.booklanguage_set}
                    lang_models={props.langModels}/>
        },
        {
            nextStepEnabled: true,
            children:
                <BookAbbreviationsEdit
                    book={formBook}
                    abbrModels={props.abbrModels}
                    abbrsByLang={abbrsByLang}/>
        },
        {
            nextStepEnabled: !formBook.booklanguage_set.some(bl => !bl.translation),
            children:
                <BookTranslationsEdit
                    book={formBook}
                    sourceLanguage={formBook.source_language}
                    langModels={props.langModels}
                    sourceTextChanged={sourceTextChanged}/>
        },
        {
            nextStepEnabled: formBook.page_size > 0,
            children:
                <div>
                    <p>
                        If your book has a lot of sentences, rendering the Read page can take a noticeable amount of time.
                        To avoid that, we break it into pages. 100 sentences per page usually works well.
                    </p>

                    <label htmlFor='page_size'>
                        Sentences per page
                    </label>
                    <Input
                        id='page_size'
                        type="number"
                        value={formBook.page_size}
                        onChange={(e) => appDispatch(bookEditorActions.setPageSize(e.target.value))}/>
                </div>
        },
        {
            nextStepEnabled: false,
            children:
                <div>
                    <p>
                        All is ready! Save the book, or preview how it will look without saving.
                    </p>

                    <Button
                        onClick={e => openPreview()}
                        children={'Preview'}/>
                    <> </>
                    <Button
                        type='submit'
                        children={'Submit'}/>
                </div>
        }
    ];

    const [currentStep, setCurrentStep] = useState(formBook.id ? stepProperties.length : 1);    
    let nextButton = <></>

    const stepComponents = [] as ReactNode[];
    for (let i = 1; i <= stepProperties.length; i++)
    {
        const currentStepProperties = stepProperties[i - 1];
        const lastStep = i >= stepProperties.length;
        const breakLoop = lastStep || !currentStepProperties.nextStepEnabled || i >= currentStep;

        let gapBetweenStages = <></>;

        if (breakLoop && !lastStep)
        {
            nextButton =
            <>
                <hr/>
                <> </>
                <Button
                    disabled={!currentStepProperties.nextStepEnabled}
                    onClick={function()
                        {
                            setCurrentStep(i + 1);

                            //Make the next step visible, and focus its first input.
                            window.setTimeout(function()
                            {
                                const elmInput = document
                                    .querySelector('.ernots_BookEdit_Step[data-last_visible_step=true]')
                                    ?.querySelector('input, button, textarea, select') as HTMLElement;

                                elmInput?.scrollIntoView();
                                elmInput?.focus()
                            }, 10);
                        }
                    }
                    children={'Next'}/>
            </>;
        }
        else
        {
            gapBetweenStages = <br/>;
        }

        const stepComponent = 
            <Fragment key={i}>
                <fieldset className='ernots_BookEdit_Step' data-last_visible_step={breakLoop}>
                    <legend>Step {i} of {stepProperties.length}</legend>

                    {currentStepProperties.children}
                    {nextButton}
                </fieldset>
                {gapBetweenStages}
            </Fragment>;
        
        stepComponents.push(stepComponent);

        if (breakLoop)
        {
            break;
        }
    }

    //Make this element have a height even without contents.
    //Because it's the last, it tends to end up out of frame.
    const statusDisplayStyle = {height: '3em'};

    //The element containing the bulk of HTML.
    main = (
    <form onSubmit={submitForm}>
        {BookEditExplanations.header}

        {stepComponents}
        <div style={statusDisplayStyle}>
            <RequestStatusDisplay request={bookUploadRequest}
                success_message={
                <>
                    Book uploaded successfully.
                    Click <Anchor to={`/book/read/?bookId=${formBook.id}&goToBookmark=1`} target="_blank">here</Anchor> to read it.
                </>}/>
            <RequestStatusDisplay
                request={bookListDownloadRequest}
                processing_message={'Refreshing book list'}
                success_message={<></>}/>
            <RequestStatusDisplay
                request={presetUploadRequest}
                success_message={'New language preset created.'}/>
        </div>
    </form>);
    
    return (
    <div className='ernots_BookEdit'>
        <title>{GetPageTitle(pageTitle)}</title>
        {main}
    </div>);
}

interface Props
{
    formBook: Book;
    preset?: BookLanguagePreset;
    abbrModels: ModelMap<Abbreviation>;
    langModels: ModelMap<Language>;
    bookModels: ModelMap<BookForList>;
    presetModels: ModelMap<BookLanguagePreset>;
}